import { ApprovalEvidence } from './ApprovalEvidence';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Drawer, DrawerFooter, ErrorBanner, useToast } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { errorView } from '@app/errors';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { useCanOperar } from '@app/auth/useCan';
import {
  isStaleVersion,
  useApproval,
  useApproveApproval,
  useRejectApproval,
  useRetryAutoApproval,
} from './Approvals.api';
import { ApproveDialog } from './ApproveDialog';
import { RejectDialog } from './RejectDialog';
import { AssignmentBlock } from './AssignmentBlock';
import { DiffTable } from './DiffTable';
import { InheritedPolicies } from './InheritedPolicies';
import { ProvenanceCard } from './ProvenanceCard';
import { ReleaseHeader } from './ReleaseHeader';
import { UnresolvedAgents } from './UnresolvedAgents';
import {
  change,
  pendencyChange,
  changeText,
  decisorLabel,
  reasonDecisionLabel,
  rolloutSuffix,
  statusLabel,
  statusTone,
  type ApprovalDetail,
} from './ApprovalsModel';
import s from './Approvals.module.css';

type Caixa = 'aprovar' | 'rejeitar' | null;

export interface Decision {
  mensagem: string;
  /** Aprovação bem-sucedida ganha o link **Ver Runtime Status** (FR-063). */
  runtime: boolean;
}

export interface ApprovalDrawerProps {
  id: string | null;
  onFechar: () => void;
  /** Avisa a tela que a fila mudou — ela mostra o resultado com o link de T15. */
  onDecidido?: (decisao: Decision) => void;
}

/**
 * T10 — o drawer de decisão (FR-059…FR-064).
 *
 * Leitura para todo mundo; decidir é `approver`/`admin` e o botão é **omitido**
 * para quem não pode (FR-010). Falha na materialização não grava nada: o banner
 * usa `escrita` para dizer isso com as palavras da matriz FR-123.
 */
export function ApprovalDrawer({ id, onFechar, onDecidido }: ApprovalDrawerProps) {
  const detalhe = useApproval(id);
  const [caixa, setCaixa] = useState<Caixa>(null);

  const payload = detalhe.data;
  const titulo = payload?.identity_slug ?? t('approvals.drawer.fallbackTitle');

  return (
    <Drawer
      open={Boolean(id)}
      title={titulo}
      subtitle={payload ? <HeaderBadges detalhe={payload} /> : undefined}
      onClose={() => {
        setCaixa(null);
        onFechar();
      }}
      footer={payload ? <Rodape detalhe={payload} caixa={caixa} setCaixa={setCaixa} /> : undefined}
    >
      {detalhe.isLoading ? <p className={s.carregando}>{t('approvals.drawer.loading')}</p> : null}

      {detalhe.isError ? (
        <ErrorBanner
          error={detalhe.error}
          title={t('approvals.drawer.error.open')}
          onRetry={() => void detalhe.refetch()}
        />
      ) : null}

      {payload ? (
        <Body
          detalhe={payload}
          caixa={caixa}
          setCaixa={setCaixa}
          onFechar={onFechar}
          onDecidido={onDecidido}
        />
      ) : null}
    </Drawer>
  );
}

// O badge do cabeçalho lê a **mesma** fonte do alerta e do rodapé
// (`mudancaDaPendencia`), e não o `motivo` da API. O `motivo` responde outra
// pergunta — identidade nova ou já existente —, e usá-lo aqui punha «capabilities
// alteradas» a dois centímetros de «Mudança de autoridade: NÃO», na mesma tela
// (ensaio de 30/08). A fila de T9 continua no `motivo`, que é o vocabulário do
// filtro dela.
function HeaderBadges({ detalhe }: { detalhe: ApprovalDetail }) {
  const readValue = change(pendencyChange(detalhe));
  return (
    <span className={s.chips}>
      <Badge tone={readValue.tone}>{readValue.rotulo}</Badge>
      <Badge tone={statusTone(detalhe.status)}>{statusLabel(detalhe)}</Badge>
    </span>
  );
}

interface BodyProps {
  detalhe: ApprovalDetail;
  caixa: Caixa;
  setCaixa: (c: Caixa) => void;
  onFechar: () => void;
  onDecidido?: (decisao: Decision) => void;
}

function Body({ detalhe, caixa, setCaixa, onFechar, onDecidido }: BodyProps) {
  const toast = useToast();
  const approve = useApproveApproval();
  const reject = useRejectApproval();
  const newRetry = useRetryAutoApproval();
  const canRetry = useCanOperar('retryAutoApproval');

  const errorDecision = approve.error ?? reject.error ?? newRetry.error ?? null;
  // A5: a pendência mudou depois de aberta. A fila e o detalhe já foram relidos
  // (`onError` do hook); o que falta é dizer à pessoa que o que ela vê agora é outra
  // versão, e não um erro de gravação — por isso um aviso próprio no lugar do banner.
  const stale = isStaleVersion(errorDecision);
  const onDecisionError = (error: ApiError) => {
    // O diálogo fecha: o motivo digitado era sobre a versão velha.
    if (isStaleVersion(error)) setCaixa(null);
  };

  // O erro pertence à TENTATIVA, não à gaveta: abrir o diálogo de novo tem de
  // começar limpo. Fechar não limpa de propósito — é o banner que fica a
  // explicar o que aconteceu depois de a pessoa carregar em Cancelar.
  useEffect(() => {
    if (caixa === 'aprovar') approve.reset();
    if (caixa === 'rejeitar') reject.reset();
    // `reset` é estável; o efeito corre quando a caixa muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caixa]);

  const confirmApproval = (motivo: string) => {
    approve.mutate(
      { id: detalhe.id, motivo, version: detalhe.version },
      {
        onError: onDecisionError,
        onSuccess: (resultado) => {
          const mensagem =
            t('approvals.drawer.approved', { version: resultado.contract.version }) +
            rolloutSuffix(resultado.deployment);
          setCaixa(null);
          // P28 — a aprovação valeu nos três alvos em qualquer um dos casos; o que muda é
          // se a imagem subiu. Em `failed` o tom é de aviso, e não de erro: erro diria que
          // a aprovação não aconteceu, e ela aconteceu.
          toast.mostrar(mensagem, {
            tone: resultado.deployment?.status === 'failed' ? 'aviso' : 'sucesso',
          });
          onDecidido?.({ mensagem, runtime: true });
          onFechar();
        },
      },
    );
  };

  const confirmRejection = (motivo: string) => {
    reject.mutate(
      { id: detalhe.id, motivo, version: detalhe.version },
      {
        onError: onDecisionError,
        onSuccess: () => {
          const mensagem = t('approvals.drawer.rejected');
          setCaixa(null);
          toast.mostrar(mensagem, { tone: 'info' });
          onDecidido?.({ mensagem, runtime: false });
          onFechar();
        },
      },
    );
  };

  return (
    <>
      <ChangeAlerta detalhe={detalhe} />
      <section className={s.secao}>
        <h3 className={s.secaoTitulo}>{t('approvals.evidence.title')}</h3>
        <ApprovalEvidence approval={detalhe} detailed />
      </section>

      {detalhe.incomplete_assignment ? <AssignmentBlock detalhe={detalhe} /> : null}

      {detalhe.auto_failed_code ? (
        <Alert
          tone="danger"
          title={t('approvals.drawer.autoFailed.title', { code: detalhe.auto_failed_code })}
        >
          {t('approvals.drawer.autoFailed.body')}
        </Alert>
      ) : null}

      {detalhe.status === 'pendente' && detalhe.auto_retry_available && !detalhe.auto_failed_code ? (
        <Alert tone="info" title={t('approvals.drawer.unconfirmed.title')}>
          {t('approvals.drawer.unconfirmed.body')}
        </Alert>
      ) : null}

      {stale ? (
        <Alert tone="warning" title={t('approvals.drawer.staleVersion.title')}>
          {t('approvals.drawer.staleVersion.body')}
        </Alert>
      ) : errorDecision ? (
        <ErrorBanner error={errorDecision} escrita title={t('approvals.drawer.error.decision')} />
      ) : null}

      <ReleaseHeader detalhe={detalhe} />
      <ProvenanceCard detalhe={detalhe} />
      <DiffTable detalhe={detalhe} />
      {/* DEF-39: o alvo declarado que não vira delegação. Fica colado ao diff —
          é o que a aprovação NÃO vai emitir, e some do lado do que vai. */}
      <UnresolvedAgents detalhe={detalhe} />
      <InheritedPolicies detalhe={detalhe} />

      {detalhe.status !== 'pendente' ? <BlockDecision detalhe={detalhe} /> : null}

      {caixa === 'aprovar' ? (
        <ApproveDialog
          enviando={approve.isPending}
          // D10: em falha o diálogo CONTINUA aberto (é o que preserva o motivo
          // já digitado), e o `ErrorBanner` do corpo da gaveta nasce ATRÁS
          // dele. Sem esta linha a pessoa vê o botão voltar a ficar ativo e
          // mais nada — medido no lab em 15/09, com um 409 real.
          erro={approve.error ? errorView(approve.error, { escrita: true }).texto : null}
          onCancelar={() => setCaixa(null)}
          onConfirmar={confirmApproval}
        />
      ) : null}

      {caixa === 'rejeitar' ? (
        <RejectDialog
          enviando={reject.isPending}
          erro={reject.error ? errorView(reject.error, { escrita: true }).texto : null}
          onCancelar={() => setCaixa(null)}
          onConfirmar={confirmRejection}
        />
      ) : null}

      {detalhe.status === 'pendente' && detalhe.auto_retry_available && canRetry && !caixa ? (
        <Button
          variant="out"
          icon="refresh"
          disabled={newRetry.isPending}
          onClick={() =>
            newRetry.mutate(
              { id: detalhe.id },
              {
                onSuccess: (resultado) => {
                  const mensagem = t('approvals.drawer.autoRetried', {
                    version: resultado.contract.version,
                  });
                  toast.mostrar(mensagem, { tone: 'sucesso' });
                  onDecidido?.({ mensagem, runtime: true });
                  onFechar();
                },
              },
            )
          }
        >
          {t('approvals.drawer.retry')}
        </Button>
      ) : null}
    </>
  );
}

/**
 * O alerta acima da dobra (FR-061).
 *
 * Era «por motivo», com os dois textos literais do inventário §3.4; passou a ser
 * **pela leitura da pendência** em 30/08. O `motivo` da API tem dois valores e
 * responde "identidade nova ou já existente?"; este alerta é o que a pessoa lê
 * primeiro, e ele tem de responder "o que mudou?" — a pergunta que a decisão dela
 * fecha. Enquanto respondia a primeira, anunciava «Capabilities alteradas» sobre
 * releases que não mudaram capability nenhuma, e contradizia o rodapé da própria
 * tela (divergência do protótipo registrada em `docs/progress/contrato.md`).
 */
function ChangeAlerta({ detalhe }: { detalhe: ApprovalDetail }) {
  const readValue = change(pendencyChange(detalhe));
  return (
    <Alert tone={readValue.alertaTone} title={readValue.titulo}>
      {changeText(detalhe)}
    </Alert>
  );
}

/** Bloco "Decisão / Por / Motivo" das aprovações já decididas (§3.4). */
function BlockDecision({ detalhe }: { detalhe: ApprovalDetail }) {
  const decided = t(
    detalhe.status === 'auto'
      ? 'approvals.drawer.decision.auto'
      : detalhe.status === 'aprovada'
        ? 'approvals.status.aprovada'
        : 'approvals.status.rejeitada',
  );
  const motivo = reasonDecisionLabel(detalhe);

  return (
    <section className={s.secao}>
      <h3 className={s.secaoTitulo}>{t('approvals.drawer.decision.title')}</h3>
      <dl className={s.dl}>
        <dt>{t('approvals.drawer.decision.decision')}</dt>
        <dd>
          <Badge tone={statusTone(detalhe.status)}>{decided}</Badge>
        </dd>
        <dt>{t('approvals.drawer.decision.by')}</dt>
        <dd>
          {decisorLabel(detalhe)}
          {detalhe.decided_at ? ` · ${dataHour(detalhe.decided_at)}` : ''}
        </dd>
        <dt>{t('approvals.drawer.decision.reason')}</dt>
        <dd className={motivo.vazio ? s.muted : undefined}>
          {motivo.vazio && detalhe.status !== 'auto'
            ? t('approvals.drawer.decision.reasonEmpty')
            : motivo.texto}
        </dd>
        {detalhe.resulting_contract_version ? (
          <>
            <dt>{t('approvals.drawer.decision.contract')}</dt>
            <dd>v{detalhe.resulting_contract_version}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

function Rodape({
  detalhe,
  caixa,
  setCaixa,
}: {
  detalhe: ApprovalDetail;
  caixa: Caixa;
  setCaixa: (c: Caixa) => void;
}) {
  const navigate = useNavigate();
  // FR-010: quem não pode decidir não vê os botões — nada de `disabled`.
  const canDecide = useCanOperar('approveApproval');
  const pendente = detalhe.status === 'pendente';
  const bloqueada = Boolean(detalhe.incomplete_assignment);

  return (
    <DrawerFooter
      start={
        <>
          <Button
            variant="out"
            icon="robot"
            onClick={() => navigate(`/identidades?drawer=${detalhe.identity_id}`)}
          >
            {t('approvals.drawer.footer.seeIdentity')}
          </Button>
          <Button variant="out" icon="play" onClick={() => navigate('/simulador')}>
            {t('approvals.drawer.footer.simulate')}
          </Button>
        </>
      }
    >
      {canDecide && pendente && !detalhe.auto_retry_available && !bloqueada && !caixa ? (
        <>
          <Button variant="danger" icon="ban" onClick={() => setCaixa('rejeitar')}>
            {t('approvals.drawer.footer.reject')}
          </Button>
          <Button icon="check" onClick={() => setCaixa('aprovar')}>
            {t('approvals.drawer.footer.approve')}
          </Button>
        </>
      ) : null}
    </DrawerFooter>
  );
}
